import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";

// Phase 10 (item 1/3 - "no approved item still a placeholder" / "one
// Accounting source of truth"): this page used to compute a "balance" by
// filtering the legacy Phase 1/2 /society/accounting/entries collection for
// voucherType "Purchase"/"Payment" rows - a collection the real Phase 3+
// engine (and every Vendor Bill/Vendor Payment voucher since Phase 5) never
// writes to. That made this operational page silently show empty or wrong
// numbers for every Society, right next to a correct "Vendor Payables
// Report" (reports/payables) built the right way in Phase 8. This now
// reuses that exact same engine-backed endpoint - each vendor's own
// dedicated ledger (1:1 since Phase 4/5), never a shared control account -
// so the two pages can never disagree.
export default function VendorPayablesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/reports/payables");
        setData(res.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load vendor payables"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rows = (data?.rows || []).filter((r) => Math.abs(r.closingPayable || 0) > 0.01).sort((a, b) => (b.closingPayable || 0) - (a.closingPayable || 0));
  const total = rows.reduce((sum, r) => sum + Number(r.closingPayable || 0), 0);
  const openLedger = (ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Vendor Payables</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Every vendor with an outstanding payable, from posted accounting activity. Total outstanding: ₹ {total.toLocaleString()}.</p>
      </div>

      {loading ? (
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-5 text-sm text-[#6B7280]">Loading...</div>
      ) : rows.length === 0 ? (
        <EmptyState>No outstanding vendor balances. Book a vendor bill from Transactions -&gt; Vendor Bills, or record a vendor payment, to see balances here.</EmptyState>
      ) : (
        <SectionCard>
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Vendor</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Payments</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.vendorId}>
                    <TableCell className="font-semibold"><button type="button" className="text-left hover:underline" onClick={() => openLedger(row.ledgerId)}>{row.vendorName}</button></TableCell>
                    <TableCell className="text-right">₹ {Number(row.bills || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">₹ {Number(row.payments || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right font-semibold">₹ {Number(row.closingPayable || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}
      <p className="text-xs text-[#6B7280]">For opening/adjustment detail and print/export, use Reports &rarr; Vendor Payables Report.</p>
    </div>
  );
}
